import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { RootState, RootDispatch } from "../../../store";
import {
  fetchWarehousesThunk,
  fetchVendorsThunk,
  fetchRawMaterialsThunk,
  fetchWarehouseNamesAndIdsThunk,
  deleteWarehouseVendorRawmaterialItemThunk,
} from "../../../store/slices/warehouseSlice";
import { fetchUserNames } from "../../../store/slices/userSlice";
import {
  IWarehouseRecord,
  WarehouseFormData,
} from "../../../models/warehouse.model";
import { IVendor } from "../../../models/vendor.model";
import { IWarehouseRawMaterial } from "../../../models/warehouseRawMaterial.model";
import PendingRequestsManager from "./PendingRequestsManager";
import WarehouseModal from "./WarehouseModal";
import VendorModal from "./VendorModal";
import RawMaterialModal from "./RawMaterialModal";
import WarehousesSection from "./WarehousesSection";
import VendorsSection from "./VendorsSection";
import RawMaterialsSection from "./RawMaterialsSection";
import ProductionUnitsSection from "./ProductionUnitsSection";
import { fetchDropdownData } from "../../../store/slices/storeInwardSlice";
import { getProductionUnits } from "../../../store/slices/Factory/factorySlice";

const IconWrapper: React.FC<{
  icon: React.ElementType;
  className?: string;
}> = ({ icon: Icon, className }) => <Icon className={className} />;

const Button: React.FC<{
  type: string;
  onClick: () => void;
  className: string;
  children: React.ReactNode;
}> = ({ type, onClick, className, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-md font-medium transition-colors ${className}`}
  >
    {children}
  </button>
);

const ManageWarehouse: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const {
    warehouses,
    vendors,
    totalWarehouseItems,
    totalWarehousePages,
    totalVendorItems,
    totalVendorPages,
    totalRawMaterialItems,
    totalRawMaterialPages,
    loading,
    error,
  } = useSelector((state: RootState) => state.warehouse);
  const { totalLots } = useSelector(
    (state: RootState) => state.managerWarehouse,
  );
  const {
    productionUnits,
    pagination: productionPagination,
    loading: factoryLoading,
  } = useSelector((state: RootState) => state.factory);
  const role = useSelector((state: RootState) => state.auth.role);

  const [modalType, setModalType] = useState<
    "factory" | "vendor" | "material" | null
  >(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    IWarehouseRecord | IVendor | IWarehouseRawMaterial | null
  >(null);
  const [activeTab, setActiveTab] = useState<
    "factory" | "vendors" | "productionUnits" | "materials" | "pendingLots"
  >("materials");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [rmSort, setRmSort] = useState<{
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }>({});

  const [warehouseForm, setWarehouseForm] = useState<
    Partial<WarehouseFormData>
  >({
    name: "",
    address: "",
    addressLine2: "",
    gstin: "",
    city: "",
    pincode: "",
    stateCode: "",
    contactPerson: "",
  });
  const [vendorForm, setVendorForm] = useState<Partial<IVendor>>({
    name: "",
    gstNumber: "",
    contactNumber: "",
    address: "",
  });
  const [rawMaterialForm, setRawMaterialForm] = useState<
    Partial<IWarehouseRawMaterial>
  >({
    type: "",
    name: "",
    sku: "",
    hsn: "",
    unit: undefined,
    unitType: "",
    quantity: undefined,
    approxDeliveryDays: 0,
    gst: undefined,
    requireSerialNumber: false,
  });
  const [warehousePage, setWarehousePage] = useState(1);
  const [vendorPage, setVendorPage] = useState(1);
  const [rawMaterialPage, setRawMaterialPage] = useState(1);
  const [productionUnitsPage, setProductionUnitsPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    type: "factory" | "vendor" | "material" | null;
    id: string | null;
    name: string;
  }>({ isOpen: false, type: null, id: null, name: "" });

  useEffect(() => {
    setWarehousePage(1);
    setVendorPage(1);
    setRawMaterialPage(1);
    setProductionUnitsPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (role !== "bdm") {
      if (activeTab === "factory") {
        dispatch(
          fetchWarehousesThunk({
            page: warehousePage,
            limit: itemsPerPage,
            search: debouncedSearch,
          }),
        );
      }

      if (activeTab === "vendors") {
        dispatch(
          fetchVendorsThunk({
            page: vendorPage,
            limit: itemsPerPage,
            search: debouncedSearch,
          }),
        );
      }

      if (activeTab === "productionUnits") {
        dispatch(
          getProductionUnits({
            params: {
              page: productionUnitsPage,
              limit: itemsPerPage,
              search: debouncedSearch,
              sortOrder: "desc",
            },
          }),
        );
      }
    }
    if (activeTab === "materials") {
      getRawMaterials();
    }
  }, [
    dispatch,
    activeTab,
    debouncedSearch,
    warehousePage,
    vendorPage,
    productionUnitsPage,
    rawMaterialPage,
    itemsPerPage,
    rmSort,
  ]);

  const getRawMaterials = async () => {
    await dispatch(
      fetchRawMaterialsThunk({
        page: rawMaterialPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        sortBy: rmSort.sortBy,
        sortOrder: rmSort.sortOrder,
        from: "factory",
      }),
    );
  };
  useEffect(() => {
    dispatch(fetchDropdownData("factory"));
    dispatch(fetchUserNames());
    if (role !== "bdm") {
      dispatch(fetchWarehouseNamesAndIdsThunk());
      dispatch(
        fetchWarehousesThunk({
          page: warehousePage,
          limit: itemsPerPage,
          search: debouncedSearch,
        }),
      );

      dispatch(
        fetchVendorsThunk({
          page: vendorPage,
          limit: itemsPerPage,
          search: debouncedSearch,
        }),
      );
    }

    dispatch(
      fetchRawMaterialsThunk({
        page: rawMaterialPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        from: "factory",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleModalOpen = (type: "factory" | "vendor" | "material") => {
    setModalType(type);
    setEditMode(false);
    setSelectedItem(null);
    setWarehouseForm({
      name: "",
      address: "",
      addressLine2: "",
      gstin: "",
      city: "",
      pincode: "",
      stateCode: "",
      contactPerson: "",
    });
    setVendorForm({ name: "", gstNumber: "", contactNumber: "", address: "" });
    setRawMaterialForm({
      type: "",
      name: "",
      sku: "",
      hsn: "",
      unit: undefined,
      usageType: "",
      quantity: undefined,
      approxDeliveryDays: 0,
      gst: undefined,
      requireSerialNumber: false,
    });
  };

  const handleEdit = (
    type: "factory" | "vendor" | "material",
    item: IWarehouseRecord | IVendor | IWarehouseRawMaterial,
  ) => {
    setModalType(type);
    setEditMode(true);
    setSelectedItem(item);
    if (type === "factory") {
      setWarehouseForm({
        name: (item as IWarehouseRecord).name,
        address: (item as IWarehouseRecord).address,
        addressLine2: (item as IWarehouseRecord).addressLine2,
        gstin: (item as IWarehouseRecord).gstin,
        city: (item as IWarehouseRecord).city,
        pincode: (item as IWarehouseRecord).pincode,
        stateCode: (item as IWarehouseRecord).stateCode,
        contactPerson: (item as IWarehouseRecord).contactPerson || "",
      });
    } else if (type === "vendor") {
      setVendorForm({
        name: (item as IVendor).name,
        gstNumber: (item as IVendor).gstNumber,
        contactNumber: (item as IVendor).contactNumber,
        address: (item as IVendor).address,
      });
    } else {
      setRawMaterialForm({
        type: (item as IWarehouseRawMaterial).type,
        name: (item as IWarehouseRawMaterial).name,
        sku: (item as IWarehouseRawMaterial).sku,
        unit: (item as IWarehouseRawMaterial).unit,
        usageType: (item as IWarehouseRawMaterial).usageType,
        quantity: (item as IWarehouseRawMaterial).quantity,
        approxDeliveryDays: (item as IWarehouseRawMaterial).approxDeliveryDays,
        gst: (item as IWarehouseRawMaterial).gst,
        hsn: (item as IWarehouseRawMaterial).hsn,
        requireSerialNumber:
          (item as IWarehouseRawMaterial).unit === "piece" &&
          ((item as IWarehouseRawMaterial).requireSerialNumber ?? false),
      });
    }
  };

  const handleModalClose = () => {
    setModalType(null);
    setEditMode(false);
    setSelectedItem(null);
    setWarehouseForm({
      name: "",
      address: "",
      addressLine2: "",
      gstin: "",
      city: "",
      pincode: "",
      stateCode: "",
      contactPerson: "",
    });
    setVendorForm({ name: "", gstNumber: "", contactNumber: "", address: "" });
    setRawMaterialForm({
      type: "",
      name: "",
      sku: "",
      hsn: "",
      unit: undefined,
      usageType: "",
      quantity: undefined,
      approxDeliveryDays: 0,
      gst: undefined,
      requireSerialNumber: false,
    });
  };

  const handleWarehousePageChange = (page: number) => {
    if (page >= 1 && page <= totalWarehousePages) {
      setWarehousePage(page);
    }
  };

  const handleVendorPageChange = (page: number) => {
    if (page >= 1 && page <= totalVendorPages) {
      setVendorPage(page);
    }
  };

  const handleRawMaterialPageChange = (page: number) => {
    if (page >= 1 && page <= totalRawMaterialPages) {
      setRawMaterialPage(page);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 800);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleDelete = (
    type: "factory" | "vendor" | "material",
    id: string,
    name: string,
  ) => {
    setDeleteConfirmation({ isOpen: true, type, id, name });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.type && deleteConfirmation.id) {
      dispatch(
        deleteWarehouseVendorRawmaterialItemThunk({
          id: deleteConfirmation.id,
          type: deleteConfirmation.type,
        }),
      );
      setDeleteConfirmation({
        isOpen: false,
        type: null,
        id: null,
        name: "",
      });
    }
  };

  return (
    <div className="min-h-screen">
      <div>
        <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Factory Management
              </h1>
            </div>
            <div className="flex space-x-3">
              {role === "admin" && (
                <Button
                  type="button"
                  onClick={() => handleModalOpen("factory")}
                  className="bg-[#0077C0] text-white hover:bg-[#005B90] flex items-center"
                >
                  <IconWrapper icon={() => <span>+</span>} className="mr-2" />
                  Add Factory
                </Button>
              )}
              <Button
                type="button"
                onClick={() => handleModalOpen("vendor")}
                className="bg-[#0077C0] text-white hover:bg-[#005B90] flex items-center"
              >
                <IconWrapper icon={() => <span>+</span>} className="mr-2" />
                Add Vendor
              </Button>
              <Button
                type="button"
                onClick={() => handleModalOpen("material")}
                className="bg-[#0077C0] text-white hover:bg-[#005B90] flex items-center"
              >
                <IconWrapper icon={() => <span>+</span>} className="mr-2" />
                Add Material
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab("materials");
                setRmSort({});
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === "materials"
                  ? "border-[#005B90] text-[#0077C0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <IconWrapper icon={() => <span>📦</span>} className="mr-2" />
              Raw Materials
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {totalRawMaterialItems}
              </span>
            </button>

            {/* <button
                  onClick={() => setActiveTab("pendingLots")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === "pendingLots"
                      ? "border-[#005B90] text-[#0077C0]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <IconWrapper icon={() => <span>📋</span>} className="mr-2" />
                  Pending Requests
                  <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                    {totalLots}
                  </span>
                </button> */}
            <button
              onClick={() => setActiveTab("factory")}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === "factory"
                  ? "border-[#005B90] text-[#0077C0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <IconWrapper icon={() => <span>🏠</span>} className="mr-2" />
              Factory
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {totalWarehouseItems}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("vendors")}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === "vendors"
                  ? "border-[#005B90] text-[#0077C0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <IconWrapper icon={() => <span>🚚</span>} className="mr-2" />
              Vendors
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {totalVendorItems}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("productionUnits")}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === "productionUnits"
                  ? "border-[#005B90] text-[#0077C0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <IconWrapper icon={() => <span>⚙️</span>} className="mr-2" />
              Production Units
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {productionPagination?.getProductionUnits?.totalCount || 0}
              </span>
            </button>
          </nav>
        </div>
      </div>
      {activeTab !== "pendingLots" && (
        <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <div className="relative rounded-md shadow-sm max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <IconWrapper
                    icon={() => <span>🔍</span>}
                    className="h-5 w-5 text-gray-500"
                  />
                </div>
                <input
                  type="text"
                  name="search"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-blue-300 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Search ${activeTab}...`}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading && <p>Loading...</p>}
        {activeTab === "factory" && (
          <WarehousesSection
            warehouses={warehouses}
            totalWarehouseItems={totalWarehouseItems}
            totalWarehousePages={totalWarehousePages}
            warehousePage={warehousePage}
            itemsPerPage={itemsPerPage}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleWarehousePageChange={handleWarehousePageChange}
          />
        )}
        {activeTab === "vendors" && (
          <VendorsSection
            vendors={vendors}
            totalVendorItems={totalVendorItems}
            totalVendorPages={totalVendorPages}
            vendorPage={vendorPage}
            itemsPerPage={itemsPerPage}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleVendorPageChange={handleVendorPageChange}
          />
        )}
        {activeTab === "materials" && (
          <RawMaterialsSection
            getRawMaterials={getRawMaterials}
            totalRawMaterialItems={totalRawMaterialItems}
            totalRawMaterialPages={totalRawMaterialPages}
            rawMaterialPage={rawMaterialPage}
            itemsPerPage={itemsPerPage}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleRawMaterialPageChange={handleRawMaterialPageChange}
            onSort={(sortBy, sortOrder) => setRmSort({ sortBy, sortOrder })}
          />
        )}
        {activeTab === "pendingLots" && <PendingRequestsManager />}
        {activeTab === "productionUnits" && (
          <ProductionUnitsSection
            productionUnits={productionUnits}
            pagination={productionPagination?.getProductionUnits}
            isLoading={factoryLoading.getProductionUnits}
            onPageChange={setProductionUnitsPage}
          />
        )}
      </div>
      <WarehouseModal
        isOpen={modalType === "factory"}
        onClose={handleModalClose}
        editMode={editMode}
        selectedItem={selectedItem as IWarehouseRecord | null}
        warehouseForm={warehouseForm}
        setWarehouseForm={setWarehouseForm}
        warehousePage={warehousePage}
        itemsPerPage={itemsPerPage}
        searchQuery={debouncedSearch}
      />
      <VendorModal
        isOpen={modalType === "vendor"}
        onClose={handleModalClose}
        editMode={editMode}
        selectedItem={selectedItem as IVendor | null}
        vendorForm={vendorForm}
        setVendorForm={setVendorForm}
        vendorPage={vendorPage}
        itemsPerPage={itemsPerPage}
        searchQuery={debouncedSearch}
      />
      <RawMaterialModal
        isOpen={modalType === "material"}
        onClose={handleModalClose}
        editMode={editMode}
        selectedItem={selectedItem as IWarehouseRawMaterial | null}
        rawMaterialForm={rawMaterialForm}
        setRawMaterialForm={setRawMaterialForm}
        rawMaterialPage={rawMaterialPage}
        itemsPerPage={itemsPerPage}
        searchQuery={debouncedSearch}
        getRawMaterials={getRawMaterials}
      />
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Delete
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-bold text-gray-800">
                {deleteConfirmation.name}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  setDeleteConfirmation({
                    isOpen: false,
                    type: null,
                    id: null,
                    name: "",
                  })
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageWarehouse;
